import { Formik } from "formik";
import { FC, useContext, useState } from "react";
import DatePicker from "react-date-picker";
import Select, { components } from "react-select";
import * as yup from "yup";

import appStyles from "../../../App.module.css";
import appConstants, { addDays, totalDaysInCurrentYear } from "../../../app-constants";
import { BusinessTravelContext } from "../../BusinessTravel";
import {
  btCurrencyProductIds,
  colourStyles,
  currencyDropDown,
  getProduct,
  noOfEmployee,
} from "../../config/bt-constants";
import { IBusinessTravelContext, IQuoteFrom, IinitialQuote } from "../../config/interfaces";
import PopoverIcon from "../PopoverIcon";
import Popup from "../Popup";
import Footer from "../common/Footer";
import SearchIcon from "../icons/SearchIcon";
import CoveredMoreThan250TripsModal from "../modal/CoveredMoreThan250TripsModal";
import LoadingModal from "../modal/LoadingModal";

interface QuoteFormProps {}

const QuoteForm: FC<QuoteFormProps> = (props: QuoteFormProps) => {
  const { next, quoteData, setQuoteData, setLoader, version, setQuoteResult, tripsData, loader, createQuote } =
    useContext(BusinessTravelContext) as IBusinessTravelContext;

  const [isMoreThan250, setIsMoreThan250] = useState(false);
  const [hasError, setHasError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const initialFormValues: IQuoteFrom = {
    host_country:
      version === "UK"
        ? tripsData?.hostCountries?.find((country: any) => country.code === "GB")
        : quoteData.host_country,
    currency: quoteData.currency,
    start_date: quoteData.start_date,
    end_date: quoteData.end_date,
    noOfTrips: quoteData.noOfTrips,
    noOfEmployee: quoteData.noOfEmployee,
  };

  const validationSchema = yup.object().shape({
    host_country: yup.object().required(),
    currency: yup.string().required(),
    start_date: yup.date().required(),
    end_date: yup.date().required(),
    noOfTrips: yup
      .object()
      .test("notEqualTo250", "Number of trips should not be more than 250", (selectedItem: any) => {
        return selectedItem.id !== -1;
      })
      .required("This field is required"),
    noOfEmployee: yup.string().required(),
  });

  const handleFormSubmit = async (values: IQuoteFrom) => {
    try {
      setLoader("quote");
      let _quoteData = { ...quoteData };

      _quoteData.host_country = values.host_country;
      _quoteData.currency = values.currency;
      _quoteData.start_date = values.start_date;
      _quoteData.end_date = values.end_date;
      _quoteData.noOfTrips = values.noOfTrips;
      _quoteData.noOfEmployee = values.noOfEmployee;

      const createQuoteResponse = await createQuote(_quoteData);
      setQuoteResult(createQuoteResponse);
      setQuoteData(_quoteData);
      next();
    } catch (e: any) {
      setHasError(e.message);
    } finally {
      setLoader(null);
    }
  };

  const style = { paddingLeft: "12px", lineHeight: 0 };
  const Control = ({ children, ...props }: any) => (
    <components.Control {...props}>
      <span style={style}>
        <SearchIcon />
      </span>
      {children}
    </components.Control>
  );

  return (
    <>
      {loader && (
        <Popup onClose={() => {}} title="" children={<LoadingModal />} closeButton={false} LoadingModal={true} />
      )}
      <div className={`${appStyles.mainContainer} ${appStyles.btW576}`}>
        <h3 className={`${appStyles.mT0}`}>Business Travel Insurance</h3>
        <Formik initialValues={initialFormValues} validationSchema={validationSchema} onSubmit={handleFormSubmit}>
          {({ values, errors, touched, handleBlur, setFieldValue, handleSubmit, isValid, validateForm, dirty }) => (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                await validateForm();
                handleSubmit();
              }}
            >
              <div className={` ${appStyles.formBox} `}>
                <div className={appStyles.formWrapper}>
                  <div className={appStyles.formRow}>
                    <div className={appStyles.formBlock}>
                      <div
                        className={`${appStyles.formField} ${
                          touched?.start_date && errors?.start_date ? appStyles.hasError : ""
                        }`}
                      >
                        <label className={appStyles.btFormLabel}>Policy start date</label>
                        <DatePicker
                          calendarClassName={`${appStyles.bfpDatePickerCalendar} ${appStyles.btDatePickerCalendar}`}
                          className={`${appStyles.bfpDatePicker} ${appStyles.btDatePicker} ${appStyles.bfpDatePickerCalendarStartDate} ${appStyles.btDatePickerCalendarStartDate}`}
                          dayPlaceholder="dd"
                          monthPlaceholder="mm"
                          yearPlaceholder="yyyy"
                          format={appConstants.datePickerFormat}
                          value={values.start_date}
                          maxDate={addDays(new Date(), 55)}
                          id="start_date"
                          isOpen={isOpen}
                          onClick={() => setIsOpen(true)}
                          onCalendarClose={() => setIsOpen(false)}
                          onChange={(value: any) => {
                            setFieldValue("start_date", value);

                            setFieldValue("end_date", addDays(value, totalDaysInCurrentYear(value)));
                          }}
                          onBlur={handleBlur("start_date")}
                          minDate={new Date()}
                        />
                        {touched?.start_date && errors?.start_date && (
                          <div className={appStyles.formError}>This field is required</div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className={appStyles.formRow}>
                    <div className={appStyles.formBlock}>
                      <label className={appStyles.btFormLabel}>Number of covered trips</label>

                      <div
                        className={`${appStyles.formField} ${
                          touched?.noOfTrips && errors?.noOfTrips ? appStyles.hasError : ""
                        }`}
                      >
                        <Select
                          components={{
                            IndicatorSeparator: () => null,
                          }}
                          styles={colourStyles}
                          isClearable={true}
                          isSearchable={true}
                          name="noOfTrips"
                          id="noOfTrips"
                          placeholder={<div style={{ color: "#A8A8A8" }}>Choose number of trips</div>}
                          options={tripsData?.numberOfTrips || []}
                          isLoading={tripsData?.numberOfTrips ? false : true}
                          onBlur={handleBlur("noOfTrips")}
                          onChange={(newValue: any | null) => {
                            setFieldValue("noOfTrips", newValue);
                            newValue?.id === -1 && setIsMoreThan250(true);
                          }}
                          getOptionValue={(option: any) => option}
                          getOptionLabel={(option: any) => {
                            return option?.id === -1 ? option.till : `${option?.from}-${option?.till}`;
                          }}
                          blurInputOnSelect={false}
                          className={appStyles.btSelect}
                          classNamePrefix="btSelect"
                          maxMenuHeight={400}
                        />
                        {touched?.noOfTrips && errors?.noOfTrips && (
                          <div className={`${appStyles.formError} `}>{errors.noOfTrips}</div>
                        )}
                      </div>

                      <p className={`  ${errors?.noOfTrips ? appStyles.btFormError : appStyles.btNoteWrap}`}>
                        Trips are counted on a per person, per trip basis ie. 3 employees on one trip counts as 3 trips.
                      </p>
                    </div>
                  </div>
                  {version === "EU" && (
                    <div className={appStyles.formRow}>
                      <div className={appStyles.formBlock}>
                        <label className={appStyles.btFormLabel}>Company address</label>

                        <div
                          className={`${appStyles.formField} ${
                            touched?.host_country && errors?.host_country ? appStyles.hasError : ""
                          }`}
                        >
                          <Select
                            components={{
                              Control,
                              DropdownIndicator: () => null,
                              IndicatorSeparator: () => null,
                            }}
                            styles={colourStyles}
                            isSearchable={true}
                            name="host_country"
                            id="host_country"
                            value={values.host_country}
                            placeholder={<div style={{ color: "#A8A8A8" }}>Search countries</div>}
                            options={tripsData?.hostCountries}
                            isLoading={tripsData?.hostCountries ? false : true}
                            onBlur={handleBlur("host_country")}
                            onChange={(_countries: any) => {
                              setFieldValue("host_country", _countries);
                            }}
                            getOptionValue={(option: any) => `${option?.code}`}
                            getOptionLabel={(option: any) => `${option?.name}`}
                            className={`${appStyles.btSelect} ${appStyles.btSearchSelect}`}
                            classNamePrefix="btSelect"
                            blurInputOnSelect={false}
                          />
                          {touched?.host_country && errors?.host_country && (
                            <div className={appStyles.formError}>This field is required</div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className={appStyles.formRow}>
                    <div className={appStyles.formBlock}>
                      <label className={appStyles.btFormLabel}>Preferred currency</label>

                      <div
                        className={`${appStyles.formField} ${appStyles.btFormField} ${
                          touched?.currency && errors?.currency ? appStyles.hasError : ""
                        } `}
                      >
                        {currencyDropDown.map((item, index) => (
                          <div key={index} className={appStyles.radioButton}>
                            <input
                              type="radio"
                              value={item.value}
                              id={item.value}
                              name="currency"
                              onChange={(e) => {
                                setFieldValue("currency", e.target.value);

                                setQuoteData((prevQuoteData: IinitialQuote) => ({
                                  ...prevQuoteData,
                                  productId:
                                    version === "UK" ? getProduct(e.target.value) : btCurrencyProductIds?.BTEUProductId,
                                }));
                              }}
                            />
                            <label className="btn btn-default" htmlFor={item.value}>
                              {item.label}
                            </label>
                          </div>
                        ))}

                        {touched?.currency && errors?.currency && (
                          <div className={appStyles.formError}>This field is required</div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className={appStyles.formRow}>
                    <div className={appStyles.formBlock}>
                      <label className={appStyles.btFormLabel}>
                        Number of employees at company
                        <PopoverIcon text={"This product is only suitable for companies with 2 or more employees."} />
                      </label>

                      <div
                        className={`${appStyles.formField} ${appStyles.btFormField} ${
                          touched?.noOfEmployee && errors?.noOfEmployee ? appStyles.hasError : ""
                        }`}
                      >
                        {noOfEmployee.map((item, index) => (
                          <div key={index} className={`${appStyles.radioButton} ${appStyles.radioButtonSmall}`}>
                            <input
                              type="radio"
                              id={item.value}
                              name="noOfEmployee"
                              value={item.value}
                              onChange={(e) => {
                                setFieldValue("noOfEmployee", e.target.value);
                              }}
                            />
                            <label className="btn btn-default" htmlFor={item.value}>
                              {item.value}
                            </label>
                          </div>
                        ))}
                        {touched?.noOfEmployee && errors?.noOfEmployee && (
                          <div className={appStyles.formError}>This field is required</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className={appStyles.formRow}>
                <div className={`${appStyles.buttonBlock}`}>
                  <button
                    className={`${appStyles.bfButton}  ${appStyles.btButton100}`}
                    type="submit"
                    disabled={!isValid || !dirty}
                  >
                    SEE YOUR QUOTE
                  </button>
                </div>
              </div>
            </form>
          )}
        </Formik>

        {hasError && (
          <div className={appStyles.formRow}>
            <div className={appStyles.battlefaceServerError}>{hasError}</div>
          </div>
        )}
        {isMoreThan250 && (
          <Popup
            onClose={() => {
              setIsMoreThan250(false);
            }}
            styleMoreDetailsModal={true}
            title=""
            children={<CoveredMoreThan250TripsModal />}
          />
        )}

        <Footer />
      </div>
    </>
  );
};

export default QuoteForm;
